import { Link } from 'react-router-dom';

import { Button, theme, breakpoints } from '@notino/react-styleguide';

import { styled } from '@linaria/react';

import { SHADEFINDER_CONFIG } from '../config';

export const ImageCircle = styled.img<{ smaller?: boolean }>`
  width: 100%;
  height: 100%;

  padding: ${({ smaller = false }) => (smaller ? '20% 15% 15%' : '0')};

  mix-blend-mode: darken;

  object-fit: contain;
`;

export const ItemContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  color: ${theme.color.text.secondary};

  @media (min-width: ${breakpoints.sm}) and (min-height: 50rem) {
    width: 20rem;
  }

  @media (min-width: ${breakpoints.xl}) and (min-height: 50rem) {
    width: 22.25rem;
  }

  @supports not (aspect-ratio: 1 / 1) {
    margin: 0 1rem;
  }
`;

export const ImageCircleWrapper = styled.button<{ background?: string }>`
  display: block;
  position: relative;

  width: 17.5rem;
  height: 17.5rem;
  aspect-ratio: 1 / 1;

  margin-bottom: 0.75rem;

  cursor: pointer;

  border-width: 0;
  border-color: transparent;
  border-radius: 50%;
  overflow: hidden;

  background-color: ${({
    background = SHADEFINDER_CONFIG.undertone.neutral.color,
  }) => background};

  @media (min-width: ${breakpoints.sm}) and (min-height: 50rem) {
    width: 20rem;
    height: 20rem;
  }

  @media (min-width: ${breakpoints.xl}) and (min-height: 50rem) {
    width: 22.25rem;
    height: 22.25rem;
  }

  @supports not (aspect-ratio: 1 / 1) {
    min-height: auto;
  }
`;

export const HowWeChooseShadesImageWrapper = styled(ImageCircleWrapper)`
  height: unset;
  margin-bottom: unset;
`;

export const Header = styled.div`
  margin-bottom: 2.5rem;
  padding: 0 1.5rem;

  display: flex;
  flex-flow: column;
  align-items: center;
  text-align: center;

  ${theme.typography.titleLarge};
`;

export const Footer = styled.div`
  text-align: center;
  margin-top: 2rem;
  padding: 0 1.5rem;

  ${theme.typography.labelRegular400};
  color: ${theme.color.text.secondary};
`;

export const SubHeader = styled.span`
  width: 100%;
  max-width: 43.75rem;

  margin-top: 0.75rem;
  padding: 0 1.5rem;

  color: ${theme.color.text.secondary};

  ${theme.typography.bodyRegular};
  font-weight: 400;
`;

export const Container = styled.div<{ justifyCenter: boolean }>`
  display: flex;
  flex-flow: column;
  overflow: auto;
  justify-content: ${({ justifyCenter }) =>
    justifyCenter ? 'center' : 'flex-start'};

  padding: 2rem 0 6rem;

  @media (min-width: ${breakpoints.md}) {
    height: calc(100% - 0.125rem);
    padding: 3rem 0 4rem;
  }

  @media (min-width: ${breakpoints.lg}) {
    height: unset;
    padding: 3rem 0 4rem;
  }

  > div {
    // header
    &:first-child {
      flex-basis: 25%;
    }

    // content
    &:nth-child(2) {
      flex-basis: 50%;

      display: flex;
      flex-flow: column;
      justify-content: flex-start;
    }

    // footer
    &:nth-child(3) {
      flex-grow: 1;

      display: flex;
      align-items: flex-end;
      justify-content: center;
    }
  }
`;

export const ItemList = styled.ul`
  width: 100%;

  position: relative;
  overflow: hidden;

  @media (min-width: ${breakpoints.lg}) {
    display: flex;
    flex-direction: row;
    justify-content: center;
  }
`;

export const ScrollWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  gap: 2rem;

  @media (min-width: ${breakpoints.lg}) {
    flex-direction: row;
    align-items: unset;

    overflow: auto;
    padding: 0 5rem;
    -ms-overflow-style: none;
    scrollbar-width: none;

    ::-webkit-scrollbar {
      display: none;
    }
  }
`;

export const ItemContent = styled.div`
  text-align: center;
  color: ${theme.color.text.secondary};

  padding: 0 1.25rem;
  @media (min-width: ${breakpoints.sm}) {
    padding: 0;
  }
`;

export const PaddingWrapper = styled.div`
  padding: 0 1.25rem;
`;

export const FullWidthWrapper = styled(PaddingWrapper)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  gap: 2rem;
  margin: 0 auto;

  width: 100%;
  @media (min-width: ${breakpoints.sm}) {
    width: 75%;
  }

  @media (min-width: ${breakpoints.sm}) {
    width: 100%;
  }

  @media (min-width: ${breakpoints.lg}) {
    flex-direction: row;
    justify-content: center;
    align-items: unset;
    text-align: left;

    gap: 4rem;
    padding: 0 1.25rem;
  }

  @media (min-width: ${breakpoints.xl}) {
    width: 75%;
  }
`;

export const ChooseShadeButton = styled(Button)`
  display: block;
  width: 100%;

  margin: 2rem auto 0;

  @media (min-width: ${breakpoints.sm}) {
    width: 75%;
  }

  @media (min-width: ${breakpoints.md}) {
    width: auto;
    margin: 3rem auto 0;
    padding: 0 4rem;
  }
`;

export const ItemLink = styled(Link)`
  width: 100%;
  color: ${theme.color.text.primary};
`;

export const Center = styled.div<{ padding?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;

  padding: ${({ padding }) => padding || '0'};
`;

export const HowWeChooseShadesContent = styled.div`
  color: ${theme.color.text.secondary};
  @media (min-width: ${breakpoints.lg}) {
    display: flex;
    flex-direction: column;
    justify-content: center;

    width: calc(100% - 22.25rem);
  }
`;

export const HowWeChooseShadesButton = styled.button`
  ${theme.typography.bodyRegular};
  color: ${theme.color.text.primary};
  margin-top: 0.7rem;
  background: transparent;
  border: none;
`;
